import { useCallback, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { NavLink, Outlet } from "react-router-dom";
import { createApiClient } from "../api/client";
import { ApiProvider, useApi } from "../api/context";
import { useSignOut } from "../auth/auth";
import { FullPageMessage } from "../components/FullPageMessage";
import { Logo } from "../components/Logo";
import { useConfig } from "../config";
import { useLoader } from "../lib/useLoader";
import { WorkspaceProvider, type Workspace } from "./workspace";

export function DashboardLayout() {
  const auth = useAuth();
  const signOut = useSignOut();
  const { apiUrl } = useConfig();
  const idToken = auth.user?.id_token;
  const client = useMemo(() => createApiClient(apiUrl, () => idToken), [apiUrl, idToken]);
  return (
    <ApiProvider client={client}>
      <DashboardShell onSignOut={() => void signOut()} />
    </ApiProvider>
  );
}

export function DashboardShell({ onSignOut, basePath = "/app" }: { onSignOut: () => void; basePath?: string }) {
  const api = useApi();
  const loadMe = useCallback(() => api.me(), [api]);
  const me = useLoader(loadMe);
  const isAdmin = me.data?.user.is_admin ?? false;
  const loadTenants = useCallback(
    () => (isAdmin ? api.listTenants() : Promise.resolve({ tenants: [] })),
    [api, isAdmin],
  );
  const tenants = useLoader(loadTenants);
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined);

  if (me.error) {
    return (
      <FullPageMessage title="We couldn't load your workspace">
        <p className="muted">{me.error.message}</p>
        <button type="button" className="btn btn-primary" onClick={me.reload}>
          Try again
        </button>
      </FullPageMessage>
    );
  }
  if (!me.data) {
    return <FullPageMessage title="Loading your workspace…" busy />;
  }

  const ownTenant = me.data.tenant;
  const selected = selectedTenantId ? tenants.data?.tenants.find((t) => t.tenant_id === selectedTenantId) : undefined;
  const workspace: Workspace = {
    me: me.data,
    tenantId: selectedTenantId,
    tenantName: selected?.name ?? ownTenant?.name ?? "No workspace",
    isAdmin,
  };

  return (
    <WorkspaceProvider value={workspace}>
      <div className="dash">
        <aside className="dash-sidebar">
          <Logo to={basePath} />
          <nav className="dash-nav" aria-label="Dashboard">
            <NavLink to={`${basePath}/links`}>Demo links</NavLink>
            <NavLink to={`${basePath}/embed`}>Website embed</NavLink>
            <NavLink to={`${basePath}/bookings`}>Booked calls</NavLink>
            {isAdmin && <NavLink to={`${basePath}/workspaces`}>Workspaces</NavLink>}
          </nav>
        </aside>
        <div className="dash-main">
          <header className="dash-topbar">
            {isAdmin && tenants.data ? (
              <label className="workspace-picker">
                Workspace
                <select
                  value={selectedTenantId ?? ""}
                  onChange={(event) => setSelectedTenantId(event.target.value || undefined)}
                >
                  <option value="">{ownTenant?.name ?? "My workspace"}</option>
                  {tenants.data.tenants
                    .filter((tenant) => tenant.tenant_id !== ownTenant?.tenant_id)
                    .map((tenant) => (
                      <option key={tenant.tenant_id} value={tenant.tenant_id}>
                        {tenant.name}
                      </option>
                    ))}
                </select>
              </label>
            ) : (
              <span className="workspace-name">{workspace.tenantName}</span>
            )}
            <div className="dash-user">
              <span className="muted">{me.data.user.email}</span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onSignOut}>
                Sign out
              </button>
            </div>
          </header>
          <main id="main" className="dash-content">
            <Outlet key={selectedTenantId ?? "own"} />
          </main>
        </div>
      </div>
    </WorkspaceProvider>
  );
}
